import Text, { TextSizes } from '@gym-atoms/Text/Text';
import Avatar from '@gym-atoms/Avatar/Avatar';
import { Dialog } from 'primereact/dialog';

import styles from './AccessCardIssueModal.module.scss';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import Input from '@gym-atoms/Input/Input';
import { MemberAccessCard } from '@gym-particles/types/Member';
import { issueAccessCard } from '@gym-graphql/mutations';
import { IssueAccessCardMutation, IssueAccessCardMutationVariables } from '@gym-src/API';
import { API } from 'aws-amplify';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { AccessPoints } from '@gym-particles/types/AccessPoints';
import { useAppSelector } from '@gym-redux/store';
import { useParams } from 'react-router';

dayjs.extend(utc);

const AccessCardIssueModal = ({ visible, onHideCallBack, member }: AccessCardIssueModalProps) => {
  const { t } = useTranslation();
  const { gymId } = useParams<{ chainId: string; gymId: string }>();

  const [isIssuing, setIsIssuing] = useState(false);
  const [cardUIDInput, setCardUIDInput] = useState('');
  const currentUser = useAppSelector((state) => state.user.userId);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [existingUserName, setExistingUserName] = useState<string>('');

  const onIssueAccessCardClick = async () => {
    setIsIssuing(true);
    setErrorMessage('');

    const cardUID = cardUIDInput.trim();

    if (cardUID.length === 0 || !/^[0-9]+$/.test(cardUID)) {
      setErrorMessage(t('ACCESS_CARD_ISSUE.INVALID_CARD_INPUT_ERROR'));
      setIsIssuing(false);
      return;
    }

    try {
      const data: IssueAccessCardMutationVariables = {
        input: {
          accessCardUID: cardUID,
          memberId: member.memberId,
          siteLocationId: +gymId,
          userId: currentUser
        }
      };
      await (API.graphql({
        query: issueAccessCard,
        variables: data
      }) as Promise<{
        data: IssueAccessCardMutation;
      }>);
      hideModal();
    } catch (error) {
      console.error(error);
      setErrorMessage(t('ACCESS_CARD_ISSUE.OTHER_ERROR'));
    } finally {
      setIsIssuing(false);
    }
  };

  const CardHeader = (
    <>
      <Text size={TextSizes.large} bold className="text-2xl">
        {t('ACCESS_CARD_ISSUE.MODAL_TITLE')}
      </Text>
      <div className="d-flex  mt5 w-100">
        <div className="mr15">
          <Avatar image={member.imageUrl} size={'default'} shape={'circle'} />
        </div>
        <div className="d-flex mt7">
          <Text size={TextSizes.t14} bold>
            {`${member.firstName} ${member.lastName}`}
          </Text>
          <p className="ml5 mr5">-</p>
          <Text size={TextSizes.t14}>
            {member.hasAccessCard
              ? t('ACCESS_CARD_ISSUE.STATUS_HAVE_ACCESS_CARD')
              : t('ACCESS_CARD_ISSUE.STATUS_NO_PREVIOUS_ACCESS_CARD')}
          </Text>
        </div>
      </div>
    </>
  );

  const cardContent = (
    <>
      {existingUserName && (
        <div className="mb25 p-error">
          <Text size={TextSizes.t15}>{`${existingUserName}`}</Text>
        </div>
      )}

      <>
        <div className="d-flex justify-content-between  mt15">
          <div className="w-75">
            <Input
              label={t('ACCESS_CARD_ISSUE.SCAN_INPUT_LABEL')}
              value={cardUIDInput}
              onChange={(e) => setCardUIDInput(e.currentTarget.value)}
              variant={'basic'}
              required
              error={errorMessage.length !== 0}
              errorMessage={errorMessage}
            />
          </div>
          <div className="ml10 mt30">
            <Button
              label={t('ACCESS_CARD_ISSUE.SCAN_BUTTON_LABEL')}
              icon={isIssuing ? 'pi pi-spin pi-spinner' : ''}
              onClick={onIssueAccessCardClick}
              variant={ButtonVariants.basic}
              size={ButtonSizes.large}
              disabled={isIssuing || cardUIDInput.length === 0}
            />
          </div>
        </div>
        <Text size={TextSizes.t15}>{t('ACCESS_CARD_ISSUE.DESCRIPTION')}</Text>
      </>
    </>
  );
  const footer = (
    <>
      <div>
        <Button
          label={t('ACCESS_CARD_ISSUE.CANCEL_BUTTON_LABEL')}
          onClick={() => hideModal()}
          variant={ButtonVariants.textonly}
          size={ButtonSizes.large}
        />
      </div>
    </>
  );

  const hideModal = () => {
    setCardUIDInput('');
    setErrorMessage('');
    setIsIssuing(false);
    setExistingUserName('');
    onHideCallBack();
  };

  return (
    <Dialog
      onHide={() => hideModal()}
      visible={visible}
      closable={true}
      dismissableMask={false}
      header={CardHeader}
      className={` ${styles.dialogBoxSize}`}
      footer={footer}>
      {cardContent}
    </Dialog>
  );
};
export default AccessCardIssueModal;

export interface AccessCardIssueModalProps {
  /* component visibility */
  visible: boolean;
  /* dropdown menu options */
  accessPoints?: AccessPoints[];
  /* action to perform on clicking close button & cancel button */
  onHideCallBack: () => void;
  /* the member to issue the access card */
  member: MemberAccessCard;
}
